import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useGetCurrentClient } from '@frontend/app/hooks';

import { useSuggestedInstagramCreatorFilters } from '../instagram/hooks/use-suggested-instagram-creator-filters';
import { INSTAGRAM_SEARCH_PARAMS } from '../instagram/types';
import {
  buildInstagramDefaultFiltersPrompt,
  generateInstagramSearchParams,
  parseInstagramSearchParams,
} from '../instagram/utils';
import { QueryVariables, SearchContext } from '../types';

interface UseCreatorSearchParamsProps {
  defaultVariables?: QueryVariables;
}

export const useCreatorSearchParams = ({ defaultVariables = {} }: UseCreatorSearchParamsProps) => {
  const location = useLocation();
  const history = useHistory();

  // Get current client information for personalized prompts
  const { client: currentClient } = useGetCurrentClient();

  // Parse search params from URL
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const searchQuery = searchParams.get(INSTAGRAM_SEARCH_PARAMS.QUERY) || '';
  const searchPrompt = searchParams.get(INSTAGRAM_SEARCH_PARAMS.PROMPT); // Keep null vs empty string distinction

  // Default filter initialization state
  const [defaultFiltersResolved, setDefaultFiltersResolved] = useState(false);
  const defaultFiltersResolvedRef = useRef(false);

  // Track if initial AI generation has been made to prevent redundant calls
  const initialAiGenerationMadeRef = useRef(false);

  // Use the AI suggestion hook
  const {
    queryFilter: suggestedQueryFilter,
    // TODO: make use of additional filters and aggregate results
    // similarToFilters: suggestedSimilarToFilters,
    // exactMatchFilters: suggestedExactMatchFilters,
    promptUsedForSuggestion,
    error: suggestedFilterError,
    generateFilter,
    isLoading: isGeneratingFilter,
    clearSuggestedFilter,
  } = useSuggestedInstagramCreatorFilters();

  // Check if there are any existing query parameters
  const hasExistingParams = Object.values(INSTAGRAM_SEARCH_PARAMS).some((param) => searchParams.has(param));

  // Build personalized prompt based on client information
  const buildInitialPrompt = useCallback(() => {
    return buildInstagramDefaultFiltersPrompt(currentClient);
  }, [currentClient]);

  // Helper function to determine the appropriate prompt value for URL inclusion
  const getPromptForUrl = useCallback((
    promptUsedForSuggestion: string | null,
    currentPrompt: string | null
  ): { prompt: string } | Record<string, never> => {
    // If promptUsedForSuggestion exists and is user-entered, include it in URL
    if (promptUsedForSuggestion !== null && promptUsedForSuggestion.trim() !== '') {
      return { prompt: promptUsedForSuggestion };
    }

    // If current prompt exists in URL (user-entered), preserve it
    if (currentPrompt !== null && currentPrompt.trim() !== '') {
      return { prompt: currentPrompt };
    }

    // No prompt in URL for system-generated defaults
    return {};
  }, []);

  // Update URL search params
  const updateUrlParams = useCallback(
    (params: Partial<QueryVariables>) => {
      const urlParams = generateInstagramSearchParams(params, new URLSearchParams(location.search));
      history.replace({ pathname: location.pathname, search: urlParams.toString() });
    },
    [history, location.pathname, location.search],
  );

  // Handle search input change
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const params = {
        ...parseInstagramSearchParams({ searchParams, searchQuery, searchPrompt, defaultVariables }),
        query: e.target.value,
      };
      updateUrlParams(params);
    },
    [updateUrlParams, defaultVariables, searchParams, searchQuery, searchPrompt],
  );

  // Clear search input
  const handleClearSearch = useCallback(() => {
    const params = {
      ...parseInstagramSearchParams({ searchParams, searchQuery, searchPrompt, defaultVariables }),
      query: '',
    };
    updateUrlParams(params);
  }, [updateUrlParams, defaultVariables, searchParams, searchQuery, searchPrompt]);

  // Update the search term in the URL
  const setSearchQuery = useCallback(
    (value: string) => {
      const params = {
        ...parseInstagramSearchParams({ searchParams, searchQuery, searchPrompt, defaultVariables }),
        query: value,
      };
      updateUrlParams(params);
    },
    [updateUrlParams, defaultVariables, searchParams, searchQuery, searchPrompt],
  );

  // Helper function to get clean default state for a specific search context
  const getCleanStateForContext = useCallback(
    (newContext: SearchContext | null, currentVariables: QueryVariables): Partial<QueryVariables> => {
      // Start with clean defaults, preserve navigation
      const cleanState = {
        ...defaultVariables,
        before: currentVariables.before,
        after: currentVariables.after,
        limit: currentVariables.limit || defaultVariables?.limit,
        searchContext: newContext,
      };

      // Context-specific preservations
      if (newContext === 'username') {
        cleanState.username = currentVariables.username || '';
      }

      if (newContext === 'aspire-ai') {
        // Only preserve prompt if it exists in URL (user-entered)
        // System-generated prompts should not appear in URL or form
        // Only assign prompt when it's non-null and non-empty to preserve null distinction for default prompt logic
        if (currentVariables.prompt !== null && currentVariables.prompt !== undefined && currentVariables.prompt.trim() !== '') {
          cleanState.prompt = currentVariables.prompt;
        }
        // If prompt is null/undefined/empty, leave cleanState.prompt as undefined to allow default prompt logic
      }

      return cleanState;
    },
    [defaultVariables],
  );

  // Update the search context in the URL
  const setSearchContext = useCallback(
    (value: SearchContext | null) => {
      const currentUrlVariables = parseInstagramSearchParams({
        searchParams,
        searchQuery,
        searchPrompt,
        defaultVariables,
      });

      // Get clean state for the new context
      const cleanState = getCleanStateForContext(value, currentUrlVariables);

      updateUrlParams(cleanState);
    },
    [updateUrlParams, searchParams, searchQuery, searchPrompt, defaultVariables, getCleanStateForContext],
  );

  // Update the search context and clear the query in a single operation
  const setSearchContextAndClearQuery = useCallback(
    (value: SearchContext | null) => {
      const currentUrlVariables = parseInstagramSearchParams({
        searchParams,
        searchQuery,
        searchPrompt,
        defaultVariables,
      });

      // Get clean state for the new context (query is already cleared in helper)
      const cleanState = getCleanStateForContext(value, currentUrlVariables);

      updateUrlParams(cleanState);
    },
    [updateUrlParams, searchParams, searchQuery, searchPrompt, defaultVariables, getCleanStateForContext],
  );

  // Initialize default filters on component mount
  useEffect(() => {
    if (!hasExistingParams) {
      // No existing params - use AI to generate default filters
      const prompt = buildInitialPrompt();
      generateFilter(prompt, false); // Mark as system-generated, not user-entered
      initialAiGenerationMadeRef.current = true;
    } else {
      // Existing params found - skip AI and mark as resolved
      setDefaultFiltersResolved(true);
      defaultFiltersResolvedRef.current = true;
    }
  }, [generateFilter, hasExistingParams, buildInitialPrompt]);

  // Apply AI-generated default filters when available
  useEffect(() => {
    if (suggestedQueryFilter) {
      // Get current context to avoid overriding manual context changes
      const currentUrlVariables = parseInstagramSearchParams({
        searchParams,
        searchQuery,
        searchPrompt,
        defaultVariables,
      });

      // Only apply AI suggestions if we're in aspire-ai context
      if (currentUrlVariables.searchContext === 'aspire-ai') {
        const suggestedQueryVariables: Partial<QueryVariables> = {
          ...defaultVariables,
          ...suggestedQueryFilter,
          searchContext: 'aspire-ai' as const,
          // For prompt handling: include user-entered prompts in URL, exclude system-generated ones
          ...getPromptForUrl(promptUsedForSuggestion, currentUrlVariables.prompt),
        };

        updateUrlParams(suggestedQueryVariables);

        // Clear the suggested filter to prevent re-application
        clearSuggestedFilter();
      }

      // Only mark as resolved if this was for initial default filters
      if (!defaultFiltersResolvedRef.current) {
        setDefaultFiltersResolved(true);
        defaultFiltersResolvedRef.current = true;
      }
    }
  }, [
    suggestedQueryFilter,
    promptUsedForSuggestion,
    updateUrlParams,
    defaultVariables,
    searchParams,
    searchQuery,
    searchPrompt,
    clearSuggestedFilter,
    buildInitialPrompt,
    getPromptForUrl,
  ]);

  // Handle AI filter generation error - fall back to empty defaults
  useEffect(() => {
    if (suggestedFilterError && !defaultFiltersResolvedRef.current) {
      setDefaultFiltersResolved(true);
      defaultFiltersResolvedRef.current = true;
    }
  }, [suggestedFilterError]);

  // Initialize default prompt when prompt is undefined/null on page load
  useEffect(() => {
    const currentUrlVariables = parseInstagramSearchParams({
      searchParams,
      searchQuery,
      searchPrompt,
      defaultVariables,
    });

    // Set default prompt if prompt is undefined/null and we're in aspire-ai context
    // Only generate if we haven't already made an initial AI generation call
    if (currentUrlVariables.searchContext === 'aspire-ai' && currentUrlVariables.prompt === null && !initialAiGenerationMadeRef.current) {
      const defaultPrompt = buildInitialPrompt();

      // DON'T update URL with default prompt - keep it out of URL
      // Just generate filters based on the default prompt without URL update
      generateFilter(defaultPrompt, false); // Mark as system-generated, not user-entered
      initialAiGenerationMadeRef.current = true;
    }
  }, [searchParams, searchQuery, searchPrompt, defaultVariables, buildInitialPrompt, generateFilter]);

  // Calculate current URL variables with defaults and validate them
  const urlVariables = useMemo<QueryVariables>(() => {
    return parseInstagramSearchParams({
      searchParams,
      searchQuery,
      searchPrompt,
      defaultVariables,
    });
  }, [searchParams, defaultVariables, searchQuery, searchPrompt]);

  const queryVariables = useMemo<QueryVariables>(() => {
    return {
      ...urlVariables,
      // Transform for backend query if needed
      limit: urlVariables.limit || defaultVariables?.limit,
    };
  }, [urlVariables, defaultVariables?.limit]);

  // Check if any search filters are active (excluding pagination)
  const hasSearchFilters = useMemo(() => {
    const defaults = defaultVariables || {};

    // Check if query is different from default
    if (queryVariables.query && queryVariables.query !== (defaults.query || '')) {
      return true;
    }

    // Check if prompt is different from default
    if (queryVariables.prompt && queryVariables.prompt !== (defaults.prompt || '')) {
      return true;
    }

    // Check if username is different from default
    if (queryVariables.username && queryVariables.username !== (defaults.username || '')) {
      return true;
    }

    // Check if creatorUsername is different from default
    if (queryVariables.creatorUsername && queryVariables.creatorUsername !== (defaults.creatorUsername || '')) {
      return true;
    }

    // Check array filters
    if (queryVariables.ageBuckets && queryVariables.ageBuckets.length > 0) return true;
    if (queryVariables.genders && queryVariables.genders.length > 0) return true;
    if (queryVariables.countries && queryVariables.countries.length > 0) return true;
    if (queryVariables.interests && queryVariables.interests.length > 0) return true;
    if (queryVariables.majorAudienceAgeBuckets && queryVariables.majorAudienceAgeBuckets.length > 0) return true;
    if (queryVariables.majorAudienceGenders && queryVariables.majorAudienceGenders.length > 0) return true;
    if (queryVariables.majorAudienceCountries && queryVariables.majorAudienceCountries.length > 0) return true;
    if (queryVariables.similarToCreators && queryVariables.similarToCreators.length > 0) return true;

    // Check numeric filters (only if they're different from defaults and not null/0)
    if (
      queryVariables.minFollowers !== null &&
      queryVariables.minFollowers !== 0 &&
      queryVariables.minFollowers !== defaults.minFollowers
    ) {
      return true;
    }

    if (
      queryVariables.maxFollowers !== null &&
      queryVariables.maxFollowers !== 0 &&
      queryVariables.maxFollowers !== defaults.maxFollowers
    ) {
      return true;
    }

    if (
      queryVariables.minEngagements !== null &&
      queryVariables.minEngagements !== 0 &&
      queryVariables.minEngagements !== defaults.minEngagements
    ) {
      return true;
    }

    if (
      queryVariables.maxEngagements !== null &&
      queryVariables.maxEngagements !== 0 &&
      queryVariables.maxEngagements !== defaults.maxEngagements
    ) {
      return true;
    }

    return false;
  }, [queryVariables, defaultVariables]);

  const handleClearAll = useCallback(() => {
    const params = {
      ...defaultVariables,
      query: '',
      prompt: '',
      username: '',
      creatorUsername: '',
    };
    updateUrlParams(params);
  }, [updateUrlParams, defaultVariables]);

  return {
    queryVariables,
    searchQuery: queryVariables.query || '',
    setSearchQuery,
    handleSearchChange,
    handleClearSearch,
    urlVariables,
    updateUrlParams,
    handleClearAll,
    hasSearchFilters,
    defaultFiltersResolved,
    setSearchContext,
    setSearchContextAndClearQuery,
    generateFilter,
    isGeneratingFilter,
    clearSuggestedFilter,
    initialAiGenerationMade: initialAiGenerationMadeRef.current,
  };
};
